/**
 * Path configuration shared by client and server.
 *
 * Everything ClinSpaceXR serves lives under one prefix, BASE_PATH: "/" in development and
 * "/spacexr/" in production (https://clinbolt.com/spacexr/). The client reads it from Vite's
 * `import.meta.env.BASE_URL`, the server from the BASE_PATH environment variable. Both pass it
 * through `normalizeBasePath` so the rest of the code can assume a leading and trailing slash.
 */

export const DEFAULT_PORT = 3100;

/** Prefix for every localStorage key; the production origin is shared with other apps. */
export const STORAGE_PREFIX = 'spacexr:';

export const API_SEGMENT = 'api';
export const WS_SEGMENT = 'ws';

/** Ensures a base path has exactly one leading and one trailing slash. */
export function normalizeBasePath(raw: string | undefined | null): string {
  let base = (raw ?? '/').trim();
  if (base === '' || base === '.') base = '/';
  if (!base.startsWith('/')) base = `/${base}`;
  if (!base.endsWith('/')) base = `${base}/`;
  return base.replace(/\/{2,}/g, '/');
}

/** Joins a base path and a relative path without producing double slashes. */
export function joinPath(base: string, ...segments: string[]): string {
  let out = normalizeBasePath(base);
  for (const segment of segments) {
    const trimmed = segment.replace(/^\/+/, '');
    if (trimmed === '') continue;
    out = out.endsWith('/') ? `${out}${trimmed}` : `${out}/${trimmed}`;
  }
  return out;
}

/** `/api/...` under the base path, e.g. `/spacexr/api/queries`. */
export function apiPath(base: string, ...segments: string[]): string {
  return joinPath(base, API_SEGMENT, ...segments);
}

/** The WebSocket path under the base path, e.g. `/spacexr/ws`. */
export function wsPath(base: string): string {
  return joinPath(base, WS_SEGMENT);
}

/** Page paths, so the client and server agree on where each page lives. */
export const PAGES = {
  app: '',
  queries: 'queries',
  admin: 'admin',
} as const;

export function pagePath(base: string, page: keyof typeof PAGES): string {
  return joinPath(base, PAGES[page]);
}

export function storageKey(name: string): string {
  return `${STORAGE_PREFIX}${name}`;
}
